/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { db } from '../core/storage/database'

/**
 * Calcule le contentHash côté client : SHA-256(text + "|" + voiceId)
 * Doit être identique à CacheService.compute_content_hash() côté backend.
 */
export async function computeContentHash(text: string, voiceId: string): Promise<string> {
  const data = new TextEncoder().encode(`${text}|${voiceId}`)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Construit un Set de tous les contentHash disponibles dans le manifest local.
 * Permet de vérifier rapidement si un hash audio est disponible.
 */
export async function buildAvailableHashSet(compilationId: string): Promise<Set<string>> {
  const manifest = await getCompilationAudioManifest(compilationId)
  const set = new Set<string>()
  if (!manifest) return set
  for (const line of manifest.lines) {
    if (line.contentHash) set.add(line.contentHash)
  }
  return set
}

// ============================================================================
// Types
// ============================================================================

export interface ManifestLine {
  index: number
  lineIndex: number
  text: string
  duration: number
  size: number
  voiceId: string
  contentHash: string
  fileHash?: string
  cached: boolean
  filename: string
  characterName?: string
}

export interface AudioManifestLocal {
  compilationId: string
  versionId: string
  totalLines: number
  totalDuration: number | null
  totalSize: number | null
  audioFormat: string
  lines: ManifestLine[]
}

// ============================================================================
// Helpers
// ============================================================================

const CACHE_NAME = 'repet-audio'

async function isBlobInCacheStorage(filePath: string): Promise<boolean> {
  try {
    const cache = await caches.open(CACHE_NAME)
    const response = await cache.match(new Request(filePath))
    return response !== undefined
  } catch {
    return false
  }
}

async function getCachedFileHash(filePath: string): Promise<string | null> {
  try {
    const cache = await caches.open(CACHE_NAME)
    const response = await cache.match(new Request(filePath))
    if (!response) return null
    return response.headers.get('X-File-Hash')
  } catch {
    return null
  }
}

// ============================================================================
// API publique
// ============================================================================

/**
 * Récupère le manifest audio local (stocké en IndexedDB) pour une compilation.
 *
 * Retourne null si la compilation n'existe pas ou si son manifest est absent/invalide.
 */
export async function getCompilationAudioManifest(
  compilationId: string
): Promise<AudioManifestLocal | null> {
  const compilation = await db.compilations.get(compilationId)
  if (!compilation) return null

  const raw = compilation.audioManifest
  if (!raw) return null

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw

    // Deux formats possibles :
    //   1. Array plat (format interne backend brut)
    //   2. Objet { compilationId, versionId, lines: [...] } (format manifest ZIP)
    if (Array.isArray(parsed)) {
      return {
        compilationId,
        versionId: compilation.textVersionId,
        totalLines: parsed.length,
        totalDuration: compilation.totalDuration ?? null,
        totalSize: compilation.totalSize ?? null,
        audioFormat: compilation.audioFormat ?? 'opus',
        lines: parsed.map((item: Record<string, unknown>, idx: number) => ({
          index: idx + 1,
          lineIndex: typeof item.lineIndex === 'number' ? item.lineIndex : idx,
          text: String(item.text ?? ''),
          duration: Number(item.duration ?? 0),
          size: Number(item.size ?? 0),
          voiceId: String(item.voiceId ?? ''),
          contentHash: String(item.contentHash ?? ''),
          cached: Boolean(item.cached),
          filename: `line_${String(idx + 1).padStart(3, '0')}.wav`,
          characterName: item.characterName ? String(item.characterName) : undefined,
        })),
      }
    }

    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.lines)) {
      return {
        compilationId: parsed.compilationId ?? compilationId,
        versionId: parsed.versionId ?? compilation.textVersionId,
        totalLines: parsed.totalLines ?? parsed.lines.length,
        totalDuration: parsed.totalDuration ?? compilation.totalDuration ?? null,
        totalSize: parsed.totalSize ?? compilation.totalSize ?? null,
        audioFormat: parsed.audioFormat ?? compilation.audioFormat ?? 'opus',
        lines: (parsed.lines as Record<string, unknown>[]).map((item, idx) => ({
          index: typeof item.index === 'number' ? item.index : idx + 1,
          lineIndex: typeof item.lineIndex === 'number' ? item.lineIndex : idx,
          text: String(item.text ?? ''),
          duration: Number(item.duration ?? 0),
          size: Number(item.size ?? 0),
          voiceId: String(item.voiceId ?? ''),
          contentHash: String(item.contentHash ?? ''),
          cached: Boolean(item.cached),
          filename: String(item.filename ?? `line_${String(idx + 1).padStart(3, '0')}.wav`),
          characterName: item.characterName ? String(item.characterName) : undefined,
        })),
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Construit un mapping characterName → voiceId depuis le manifest local.
 * Source de vérité = le voice_mapping utilisé au moment de la compilation côté backend.
 * Permet à injectAudioHash de retrouver le voiceId exact sans dépendre des
 * voiceAssignments frontend (qui peuvent différer si les voix ont été réassignées).
 */
export async function buildCharVoiceMap(compilationId: string): Promise<Map<string, string>> {
  const manifest = await getCompilationAudioManifest(compilationId)
  const map = new Map<string, string>()
  if (!manifest) return map

  for (const line of manifest.lines) {
    if (line.characterName && line.voiceId && !map.has(line.characterName)) {
      map.set(line.characterName, line.voiceId)
    }
  }

  return map
}

/**
 * Construit deux mappings depuis le manifest local :
 *   1. "text|voiceId" → contentHash  (clé primaire — identique au SHA256 backend)
 *   2. "text|characterName" → contentHash  (clé de fallback — évite la dépendance aux
 *      voiceAssignments frontend qui peuvent être vides ou non encore chargés)
 *
 * La clé secondaire utilise le characterName stocké dans le manifest backend
 * (ex: "Jean", "VOIX_OFF") pour matcher les items de la playbackSequence sans
 * avoir à résoudre voiceId via les voiceAssignments de la version.
 */
export async function buildTextVoiceHashMap(compilationId: string): Promise<Map<string, string>> {
  const manifest = await getCompilationAudioManifest(compilationId)
  const primary = new Map<string, string>()

  if (!manifest) return primary

  for (const line of manifest.lines) {
    if (!line.contentHash || !line.text || !line.voiceId) continue
    primary.set(`${line.text.trim()}|${line.voiceId.trim()}`, line.contentHash)
  }

  return primary
}

/**
 * Construit un mapping contentHash → duration (secondes) depuis le manifest local.
 * Utilisé pour calculer le temps de lecture restant par item/réplique pendant la lecture audio.
 */
export async function buildHashDurationMap(compilationId: string): Promise<Map<string, number>> {
  const manifest = await getCompilationAudioManifest(compilationId)
  const map = new Map<string, number>()
  if (!manifest) return map

  for (const line of manifest.lines) {
    if (line.contentHash && line.duration > 0) {
      map.set(line.contentHash, line.duration)
    }
  }

  return map
}

/**
 * @deprecated Utiliser buildTextVoiceHashMap — immune aux décalages lineIndex.
 */
export async function buildLineHashMap(compilationId: string): Promise<Map<number, string>> {
  const manifest = await getCompilationAudioManifest(compilationId)
  const map = new Map<number, string>()
  if (!manifest) return map

  for (const line of manifest.lines) {
    if (line.contentHash) {
      map.set(line.lineIndex, line.contentHash)
    }
  }

  return map
}

/**
 * Vérifie qu'une compilation est entièrement disponible localement :
 *   - status === COMPLETED
 *   - manifest présent et parsable
 *   - chaque contentHash présent dans db.audioCache ET dans CacheStorage
 *
 * Retourne true uniquement si toutes les conditions sont satisfaites.
 */
export async function validateCompilationAudio(compilationId: string): Promise<boolean> {
  const compilation = await db.compilations.get(compilationId)
  if (!compilation) return false
  if (compilation.status !== 'COMPLETED') return false

  const manifest = await getCompilationAudioManifest(compilationId)
  if (!manifest || manifest.lines.length === 0) return false

  // Filtrer les lignes qui ont un hash (certaines didascalies pourraient en être dépourvues)
  const linesWithHash = manifest.lines.filter((l) => l.contentHash && l.contentHash.length > 0)
  if (linesWithHash.length === 0) return false

  for (const line of linesWithHash) {
    const cached = await db.audioCache.get(line.contentHash)
    if (!cached) return false

    const inStorage = await isBlobInCacheStorage(cached.filePath)
    if (!inStorage) return false

    // Si le manifest local contient un fileHash (SHA-256 du WAV binaire),
    // vérifier que le blob en cache correspond. Un mismatch indique que le
    // serveur a régénéré l'audio (ex: après correction TTS) → forcer re-téléchargement.
    if (line.fileHash) {
      const cachedFileHash = await getCachedFileHash(cached.filePath)
      if (cachedFileHash && cachedFileHash !== line.fileHash) {
        return false
      }
    }
  }

  return true
}

/**
 * Vérifie quels hash du manifest sont manquants localement.
 * Retourne la liste des hashes absents de IndexedDB ou de CacheStorage.
 * Utilisé pour reprendre un téléchargement partiel.
 */
export async function getMissingHashes(compilationId: string): Promise<string[]> {
  const manifest = await getCompilationAudioManifest(compilationId)
  if (!manifest) return []

  const missing: string[] = []

  for (const line of manifest.lines) {
    if (!line.contentHash) continue

    const cached = await db.audioCache.get(line.contentHash)
    if (!cached) {
      missing.push(line.contentHash)
      continue
    }

    const inStorage = await isBlobInCacheStorage(cached.filePath)
    if (!inStorage) {
      missing.push(line.contentHash)
    }
  }

  return missing
}

/**
 * Marque une compilation locale comme PARTIALLY_DOWNLOADED.
 * Appelé quand le téléchargement échoue après tous les retries.
 */
export async function markCompilationPartiallyDownloaded(
  compilationId: string,
  reason = 'Some audio files failed to download'
): Promise<void> {
  const compilation = await db.compilations.get(compilationId)
  if (!compilation) return

  await db.compilations.update(compilationId, {
    status: 'PARTIALLY_DOWNLOADED',
    error: reason,
  })
}

/**
 * Enregistre le manifest audio (JSON parsé depuis le ZIP) dans IndexedDB
 * sur la compilation locale. Appelé après téléchargement ZIP réussi.
 */
export async function storeAudioManifest(
  compilationId: string,
  manifest: AudioManifestLocal
): Promise<void> {
  const compilation = await db.compilations.get(compilationId)
  if (!compilation) return

  await db.compilations.update(compilationId, {
    // On stocke le manifest sérialisé dans le champ audioManifest
    // Le type Compilation ne définit pas encore ce champ : cast nécessaire
    ...({ audioManifest: JSON.stringify(manifest) } as Partial<typeof compilation>),
  })
}
